import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { fetchPlayerInteractionSummary } from '../../services/sessionReviewService';
import { getGenderTextColor } from '../../utils/genderColors';
import { EmptyState, LoadingState, StatusBadge } from '../common';
import { Text } from '../common/Typography';

const CompactStat = ({ label, value }) => (
  <div className="flex items-baseline justify-between gap-2 sm:block sm:text-right">
    <span className="text-xs text-text-muted sm:sr-only">{label}</span>
    <span className="text-sm font-semibold tabular-nums text-text-primary">{value}</span>
  </div>
);

const frequentMatchupLabel = (count) => (
  count === 1 ? '1 person in 50%+ of games' : `${count} people in 50%+ of games`
);

const getPlayerInitials = (name) => {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
};

const getPlayerAvatarClasses = (gender) => {
  switch (gender?.toLowerCase()) {
    case 'female':
      return 'bg-female/15 text-female border-female/25';
    case 'male':
      return 'bg-primary/10 text-primary border-primary/20';
    default:
      return 'bg-surface-subtle text-text-secondary border-border-medium';
  }
};

const SessionReviewPlayerSummaryTable = ({ sessionId, simulationData = null, filterPlayerIds, layout = 'default' }) => {
  // Stabilize filter array to avoid rerunning effects when parent doesn't pass one
  const emptyFilterRef = useRef([]);
  const effectiveFilterIds = useMemo(
    () => filterPlayerIds ?? emptyFilterRef.current,
    [filterPlayerIds]
  );
  const [summaries, setSummaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Apply player filter to fetched summaries
  const displayedSummaries = useMemo(() => {
    if (!effectiveFilterIds || effectiveFilterIds.length === 0) return summaries;
    return summaries.filter(summary => effectiveFilterIds.includes(summary.player_id));
  }, [summaries, effectiveFilterIds]);

  const loadSummary = useCallback(async () => {
    if (!sessionId) {
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      const data = await fetchPlayerInteractionSummary(sessionId);
      setSummaries(data);
    } catch (err) {
      console.error('Error loading player interaction summary:', err);
      setError('Failed to load player interaction summary');
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    if (simulationData) {
      // Use simulation data directly
      let filteredSummaries = simulationData;
      
      // Apply player filter if provided
      if (effectiveFilterIds.length > 0) {
        filteredSummaries = simulationData.filter(summary => {
          return effectiveFilterIds.includes(summary.player_id);
        });
      }
      
      setSummaries(filteredSummaries);
      setLoading(false);
      setError(null);
    } else if (sessionId) {
      loadSummary();
    } else {
      setLoading(false);
    }
  }, [sessionId, simulationData, effectiveFilterIds, loadSummary]);

  if (loading) {
    return layout === 'compact'
      ? <LoadingState message="Loading player rotation…" />
      : (
        <div className="text-center py-6 text-text-muted">
          Loading player interaction summary...
        </div>
      );
  }

  if (error) {
    return layout === 'compact'
      ? <EmptyState title="Could not load players" description={error} />
      : (
        <div className="text-center py-6 text-danger">
          {error}
        </div>
      );
  }

  if (displayedSummaries.length === 0) {
    return layout === 'compact'
      ? <EmptyState title="No players to show" description="No player data found for this session." />
      : (
        <div className="text-center py-6 text-text-muted">
          No player data found for this session.
        </div>
      );
  }

  if (layout === 'compact') {
    const flaggedCount = displayedSummaries.filter((s) => s.high_interaction_count > 0).length;
    const compactSummaries = [...displayedSummaries].sort((a, b) => {
      const flagDelta = Number(b.high_interaction_count > 0) - Number(a.high_interaction_count > 0);
      if (flagDelta !== 0) return flagDelta;
      return (a.player_name || '').localeCompare(b.player_name || '');
    });

    return (
      <div>
        <Text className="mb-3 text-xs text-text-secondary">
          {compactSummaries.length} player{compactSummaries.length === 1 ? '' : 's'}
          {flaggedCount > 0
            ? ` · ${flaggedCount} met someone in half their games`
            : ' · nobody kept meeting the same people'}
        </Text>

        <div className="overflow-hidden rounded-xl border border-border-medium">
          <div
            className="hidden gap-3 border-b border-border-medium bg-surface-subtle px-4 py-2 text-[10px] font-medium uppercase tracking-wide text-text-muted sm:grid sm:grid-cols-[minmax(0,1fr)_4.5rem_5.5rem_5.5rem_8.5rem]"
            aria-hidden
          >
            <span>Player</span>
            <span className="text-right">Games</span>
            <span className="text-right">Teammates</span>
            <span className="text-right">Opponents</span>
            <span className="text-right">Repeat people</span>
          </div>
          <ul className="divide-y divide-border-medium">
            {compactSummaries.map((summary) => {
              const flagged = summary.high_interaction_count > 0;
              return (
                <li
                  key={summary.player_id}
                  className={`px-3 py-3 sm:px-4 ${flagged ? 'bg-warning-light/20' : 'bg-surface'}`}
                >
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-[minmax(0,1fr)_4.5rem_5.5rem_5.5rem_8.5rem] sm:items-center sm:gap-3">
                    <div className="flex min-w-0 items-center gap-3">
                      <div
                        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full border text-xs font-bold ${getPlayerAvatarClasses(summary.player_gender)}`}
                        aria-hidden
                      >
                        {getPlayerInitials(summary.player_name)}
                      </div>
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1">
                          <span className={`text-sm font-semibold ${getGenderTextColor(summary.player_gender)}`}>
                            {summary.player_name || 'Unknown'}
                          </span>
                          {summary.altered_games_count > 0 ? (
                            <StatusBadge variant="warning" size="small" title="Altered games">
                              {summary.altered_games_count} altered
                            </StatusBadge>
                          ) : null}
                        </div>
                        <div className="mt-0.5 flex flex-wrap gap-1">
                          {summary.mixed_category ? (
                            <span className="text-xs text-text-muted">Mixed {summary.mixed_category}</span>
                          ) : null}
                          {summary.levels_category ? (
                            <span className="text-xs text-text-muted">
                              {summary.mixed_category ? '· ' : ''}Levels {summary.levels_category}
                            </span>
                          ) : null}
                        </div>
                      </div>
                    </div>
                    <p className="text-xs text-text-secondary sm:hidden">
                      {summary.total_games} games · {summary.unique_played_with} teammates · {summary.unique_played_against} opponents
                    </p>
                    <div className="hidden sm:block">
                      <CompactStat label="Games" value={summary.total_games} />
                    </div>
                    <div className="hidden sm:block">
                      <CompactStat label="Teammates" value={summary.unique_played_with} />
                    </div>
                    <div className="hidden sm:block">
                      <CompactStat label="Opponents" value={summary.unique_played_against} />
                    </div>
                    <div className="sm:text-right">
                      {flagged ? (
                        <span
                          className="text-xs font-medium leading-snug text-danger-dark"
                          title="Shared a court with these people in 50% or more of their games, as a partner or opponent"
                        >
                          {frequentMatchupLabel(summary.high_interaction_count)}
                        </span>
                      ) : (
                        <span className="hidden text-xs text-text-muted sm:inline">None</span>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="lg:hidden space-y-3">
        {displayedSummaries.map((summary) => (
          <div
            key={summary.player_id}
            className={`rounded-lg border border-border-medium p-4 space-y-3 ${
              summary.high_interaction_count > 0 ? 'bg-warning-light/10' : 'bg-surface'
            }`}
          >
            <div className="text-sm font-medium">
              <span className={getGenderTextColor(summary.player_gender)}>
                {summary.player_name || 'Unknown'}
              </span>
              {summary.altered_games_count > 0 && (
                <span className="text-warning font-bold ml-1" title={`${summary.altered_games_count} altered game(s)`}>
                  {'*'.repeat(summary.altered_games_count)}
                </span>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {summary.mixed_category ? (
                <span
                  className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-female-light text-female"
                  title="Mixed Doubles Category"
                >
                  {summary.mixed_category}
                </span>
              ) : null}
              {summary.levels_category ? (
                <span
                  className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-info-light text-info-dark"
                  title="Levels Category"
                >
                  {summary.levels_category}
                </span>
              ) : null}
              {!summary.mixed_category && !summary.levels_category && (
                <span className="text-xs text-text-muted">No categories</span>
              )}
            </div>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div>
                <div className="text-xs text-text-muted">Total games</div>
                <div className="font-medium text-text-primary">{summary.total_games}</div>
              </div>
              <div>
                <div className="text-xs text-text-muted">High interactions</div>
                <div className="font-medium text-text-primary">
                  {summary.high_interaction_count > 0 ? (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-danger-light text-danger-dark">
                      {summary.high_interaction_count}
                    </span>
                  ) : (
                    '0'
                  )}
                </div>
              </div>
              <div>
                <div className="text-xs text-text-muted">Played with</div>
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-success-light text-success-dark">
                  {summary.unique_played_with}
                </span>
              </div>
              <div>
                <div className="text-xs text-text-muted">Played against</div>
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-info-light text-info-dark">
                  {summary.unique_played_against}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    <div className="hidden lg:block overflow-x-auto">
      <table className="min-w-full divide-y divide-border-medium">
        <thead className="bg-surface-subtle">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase tracking-wider">
              Player
            </th>
            <th className="px-4 py-3 text-center text-xs font-medium text-text-muted uppercase tracking-wider">
              Categories
            </th>
            <th className="px-4 py-3 text-center text-xs font-medium text-text-muted uppercase tracking-wider">
              Total Games
            </th>
            <th className="px-4 py-3 text-center text-xs font-medium text-text-muted uppercase tracking-wider">
              Unique Played With
            </th>
            <th className="px-4 py-3 text-center text-xs font-medium text-text-muted uppercase tracking-wider">
              Unique Played Against
            </th>
            <th className="px-4 py-3 text-center text-xs font-medium text-text-muted uppercase tracking-wider">
              High Interaction Count
            </th>
          </tr>
        </thead>
        <tbody className="bg-surface divide-y divide-border-medium">
          {displayedSummaries.map((summary) => (
            <tr 
              key={summary.player_id} 
              className={`hover:bg-surface-subtle ${
                summary.high_interaction_count > 0 ? 'bg-warning-light/10' : ''
              }`}
            >
              <td className="px-4 py-3 whitespace-nowrap text-sm font-medium">
                <span className={getGenderTextColor(summary.player_gender)}>
                  {summary.player_name || 'Unknown'}
                </span>
                {summary.altered_games_count > 0 && (
                  <span className="text-warning font-bold ml-1" title={`${summary.altered_games_count} altered game(s)`}>
                    {'*'.repeat(summary.altered_games_count)}
                  </span>
                )}
              </td>
              <td className="px-4 py-3 whitespace-nowrap text-center text-sm text-text-primary">
                <div className="flex items-center justify-center gap-2">
                  {summary.mixed_category ? (
                    <span 
                      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-female-light text-female"
                      title="Mixed Doubles Category"
                    >
                      {summary.mixed_category}
                    </span>
                  ) : null}
                  {summary.levels_category ? (
                    <span 
                      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-info-light text-info-dark"
                      title="Levels Category"
                    >
                      {summary.levels_category}
                  </span>
                  ) : null}
                  {!summary.mixed_category && !summary.levels_category && (
                  <span className="text-text-muted">—</span>
                )}
                </div>
              </td>
              <td className="px-4 py-3 whitespace-nowrap text-center text-sm text-text-primary">
                {summary.total_games}
              </td>
              <td className="px-4 py-3 whitespace-nowrap text-center">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-success-light text-success-dark">
                  {summary.unique_played_with}
                </span>
              </td>
              <td className="px-4 py-3 whitespace-nowrap text-center">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-info-light text-info-dark">
                  {summary.unique_played_against}
                </span>
              </td>
              <td className="px-4 py-3 whitespace-nowrap text-center">
                {summary.high_interaction_count > 0 ? (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-danger-light text-danger-dark">
                    {summary.high_interaction_count}
                  </span>
                ) : (
                  <span className="text-sm text-text-muted">0</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
      <div className="mt-4 text-xs text-text-muted">
        <p className="mb-1"><strong>High Interaction Count:</strong> Number of players this player interacted with in 50% or more of their games.</p>
        <p className="mb-1"><strong>Asterisks (*):</strong> Indicates the number of altered games this player participated in (e.g., * = 1 altered game, ** = 2 altered games).</p>
        <p>Players with high interaction counts (highlighted in amber) may have had limited game variety.</p>
      </div>
    </div>
  );
};

export default SessionReviewPlayerSummaryTable;

